<template>
  <div id="app">
    <nav class="navbar navbar-expand-lg navbar-dark py-1 header-nav">
      <div class="container">
        <router-link class="navbar-brand d-flex align-items-center py-1" to="/">
          <img src="@/assets/Streamline-logo.svg" alt="Streamline Logo" class="navbar-logo" />
        </router-link>
        <button class="navbar-toggler" type="button" @click="toggleNavbar">
          <span class="navbar-toggler-icon"></span>
        </button>
        <div class="collapse navbar-collapse" :class="{ 'show': isNavbarExpanded }" id="navbarNav">
          <ul class="navbar-nav ms-auto">
            <li class="nav-item">
              <router-link class="nav-link" to="/" @click="closeNavbar">Home</router-link>
            </li>
            <li class="nav-item" v-if="!isLoggedIn">
              <router-link class="nav-link" to="/login" @click="closeNavbar">Login</router-link>
            </li>
            <li class="nav-item" v-if="isLoggedIn">
              <router-link class="nav-link" to="/dashboard" @click="closeNavbar">Dashboard</router-link>
            </li>
            <li class="nav-item" v-if="isLoggedIn">
              <router-link class="nav-link" to="/invoices" @click="closeNavbar">
                <i class="bi bi-receipt me-1"></i>Invoices
              </router-link>
            </li>
            <li class="nav-item" v-if="isLoggedIn && isAdmin">
              <router-link class="nav-link" to="/users" @click="closeNavbar">
                <i class="bi bi-people me-1"></i>Users
              </router-link>
            </li>
            <li class="nav-item dropdown" v-if="isLoggedIn">
              <a class="nav-link dropdown-toggle" href="#" id="accountDropdown" role="button" 
                 data-bs-toggle="dropdown" aria-expanded="false">
                <i class="bi bi-person-circle me-1"></i>Account
              </a>
              <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="accountDropdown">
                <li><router-link class="dropdown-item" to="/account">
                  <i class="bi bi-gear me-2"></i>Manage Account
                </router-link></li>
                <li><a class="dropdown-item" href="#" @click.prevent="clearChatSession">
                  <i class="bi bi-trash me-2"></i>Clear Chat
                </a></li>
                <li><hr class="dropdown-divider"></li>
                <li><a class="dropdown-item" href="#" @click.prevent="logout">
                  <i class="bi bi-box-arrow-right me-2"></i>Logout
                </a></li>
              </ul>
            </li>
            <li class="nav-item ms-2">
              <button @click="toggleTheme" class="btn btn-outline-light theme-toggle">
                <i :class="isDarkTheme ? 'bi bi-sun' : 'bi bi-moon'"></i>
              </button>
            </li>
          </ul>
        </div>
      </div>
    </nav>
    
    <div class="container mt-4">
      <router-view />
    </div>
    
    <!-- Chat widget - Only shown when logged in -->
    <ChatwootWidget v-if="isLoggedIn" ref="chatwootWidget" :key="currentUser?.id" :accountNumber="currentUser?.account" />
    
    <footer class="py-4 mt-5 footer-custom">
      <div class="container text-center">
        <p>&copy; {{ new Date().getFullYear() }} Streamline Telecom. All rights reserved.</p>
      </div>
    </footer>
  </div>
</template>

<script>
import { computed, ref, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import ChatwootWidget from '@/components/chat/ChatwootWidget.vue'
// Import Bootstrap components directly
import { Dropdown, Collapse } from 'bootstrap'

export default {
  name: 'App',
  components: {
    ChatwootWidget
  },
  setup() {
    const store = useStore()
    const router = useRouter()
    const refs = ref({
      chatwootWidget: null
    })
    const isNavbarExpanded = ref(false)
    const previousLoginState = ref(false)
    
    // Function to toggle navbar in mobile view
    const toggleNavbar = () => {
      isNavbarExpanded.value = !isNavbarExpanded.value
    }
    
    // Close navbar when a link is clicked
    const closeNavbar = () => {
      if (isNavbarExpanded.value) {
        isNavbarExpanded.value = false
      }
    }
    
    onMounted(() => {
      // Initialize Bootstrap dropdowns
      document.querySelectorAll('.dropdown-toggle').forEach(dropdownToggle => {
        new Dropdown(dropdownToggle);
      });
      
      // Store initial login state
      previousLoginState.value = store.getters['auth/isLoggedIn'];
      
      // Clean up any leftover Chatwoot elements if not logged in
      if (!previousLoginState.value) {
        cleanupChatwootElements();
      }
    })
    
    // Function to clean up any leftover Chatwoot elements
    const cleanupChatwootElements = () => {
      // Specifically target the main widget holder
      const widgetHolder = document.getElementById('cw-widget-holder');
      if (widgetHolder) {
        try {
          widgetHolder.parentNode.removeChild(widgetHolder);
        } catch (e) {
          console.error('Error removing cw-widget-holder:', e);
        }
      }
      
      // Remove any Chatwoot DOM elements
      const chatwootWidgets = document.querySelectorAll('[class*="chatwoot"], [id*="chatwoot"], .woot--bubble-holder');
      chatwootWidgets.forEach(el => {
        try {
          if (el.parentNode) {
            el.parentNode.removeChild(el);
          }
        } catch (e) {
          console.error('Error removing chatwoot element:', e);
        }
      });
      
      // Remove script tag
      const chatwootScript = document.querySelector('script[src*="chat.invoicedatahub.com/packs/js/sdk.js"]');
      if (chatwootScript) {
        chatwootScript.remove();
      }
      
      // Clear cookies
      document.cookie = '_chatwoot_session=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      document.cookie = '_chatwoot_session=; Path=/; Domain=chat.invoicedatahub.com; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      document.cookie = 'cw_conversation=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      document.cookie = 'cw_conversation=; Path=/; Domain=chat.invoicedatahub.com; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      
      // Clean up global objects if they exist
      if (window.$chatwoot) delete window.$chatwoot;
      if (window.chatwootSDK) delete window.chatwootSDK;
      if (window.chatwootSettings) delete window.chatwootSettings;
    }
    
    // Setup computed properties
    const isLoggedIn = computed(() => store.getters['auth/isLoggedIn'])
    const currentUser = computed(() => store.getters['auth/currentUser'])
    const isAdmin = computed(() => currentUser.value?.role === 'admin')
    
    // Watch for login state changes
    watch(isLoggedIn, (newLoginState, oldLoginState) => {
      // If logging out, ensure Chatwoot is properly cleaned up
      if (oldLoginState && !newLoginState) {
        cleanupChatwootElements();
      }
      
      previousLoginState.value = newLoginState;
    })
    
    // Theme functionality
    const isDarkTheme = ref(document.documentElement.getAttribute('data-theme') === 'dark')
    
    const toggleTheme = () => {
      const newTheme = isDarkTheme.value ? 'light' : 'dark'
      document.documentElement.setAttribute('data-theme', newTheme)
      localStorage.setItem('theme', newTheme)
      isDarkTheme.value = !isDarkTheme.value
    }
    
    const logout = () => {
      // Clean up chat widget and cookies before logout
      if (window.$chatwoot) {
        try {
          window.$chatwoot.toggleBubbleVisibility('hide');
        } catch (e) {
          console.error('Error hiding chatwoot widget:', e);
        }
      }
      
      // Clear Chatwoot session cookies
      document.cookie = '_chatwoot_session=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      document.cookie = '_chatwoot_session=; Path=/; Domain=chat.invoicedatahub.com; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      document.cookie = 'cw_conversation=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      document.cookie = 'cw_conversation=; Path=/; Domain=chat.invoicedatahub.com; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      
      // Run our cleanup to handle the widget-holder div specifically
      cleanupChatwootElements();
      
      // Then do the normal logout flow
      store.dispatch('auth/logout')
      
      // Check if we're on the home page - if so, refresh the page instead of just redirecting
      if (router.currentRoute.value.path === '/') {
        window.location.href = '/login';
      } else {
        router.push('/login');
      }
    }
    
    const clearChatSession = () => {
      // Clear Chatwoot session cookies (covers various domains and paths to be thorough)
      document.cookie = '_chatwoot_session=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      document.cookie = '_chatwoot_session=; Path=/; Domain=chat.invoicedatahub.com; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      document.cookie = 'cw_conversation=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      document.cookie = 'cw_conversation=; Path=/; Domain=chat.invoicedatahub.com; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      
      // Show a custom notification toast
      const toastEl = document.createElement('div');
      toastEl.className = 'custom-toast position-fixed bottom-0 end-0 m-3 show';
      toastEl.innerHTML = `
        <div class="custom-toast-header bg-success text-white">
          <i class="bi bi-check-circle me-2"></i>
          <strong class="me-auto">Success</strong>
          <button type="button" class="custom-close" onclick="this.parentElement.parentElement.remove()">×</button>
        </div>
        <div class="custom-toast-body">
          Chat session cleared. Refreshing page...
        </div>
      `;
      
      document.body.appendChild(toastEl);
      
      // Refresh the page after 1 second to ensure cookies are fully cleared
      // This is the most reliable way to reset the Chatwoot widget completely
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
    
    return {
      isLoggedIn,
      isAdmin,
      currentUser,
      logout,
      clearChatSession,
      isDarkTheme,
      toggleTheme,
      toggleNavbar,
      isNavbarExpanded,
      refs
    }
  }
}
</script>

<style>
#app {
  font-family: 'Roboto', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.container {
  flex: 1;
}

footer {
  margin-top: auto;
}

.footer-custom {
  background-color: var(--header-bg);
  color: var(--text-color);
  border-top: 1px solid var(--border-color);
}

.header-nav {
  background-color: var(--primary-color);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.navbar-logo {
  height: 80px;
  width: auto;
  filter: brightness(0) invert(1);
  margin-top: -14px;
  margin-bottom: -14px;
}

.theme-toggle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Main content spacing */
main {
  min-height: 60vh;
}

/* Card styling */
.card {
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s;
}

.card:hover {
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
}

/* Button styling */
.btn {
  border-radius: 6px;
  padding: 0.5rem 1.5rem;
  font-weight: 500;
  transition: all 0.3s;
}

/* Animate page transitions */
.router-view-container {
  position: relative;
}

.page-enter-active,
.page-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Navbar collapse animation */
.navbar-collapse {
  transition: height 0.35s ease;
}
</style>
